import { Currency } from '../currency';
import { throwErrorUnlessProduction } from '../errors/throwErrorUnlessProduction';
import type { MonetaryValue } from './monetaryValue';

export * from './monetaryValue';
export * from './monetaryValueBackendTypes';

export class MonetaryValueHelper {
    private readonly mv: MonetaryValue;

    private constructor(mv: MonetaryValue) {
        this.mv = mv;
    }

    private safeParseAmount(): number {
        const num = Number(this.mv.amount);
        if (isNaN(num) || !isFinite(num)) {
            throwErrorUnlessProduction(
                new Error(`MonetaryValueHelper: invalid amount "${this.mv.amount}" for currency ${this.mv.currency}`),
            );
            return 0;
        }
        return num;
    }

    private assertCurrency(other: MonetaryValue, method: string): boolean {
        if (this.mv.currency !== other.currency) {
            throwErrorUnlessProduction(
                new Error(`MonetaryValueHelper.${method}: currency mismatch ${this.mv.currency} vs ${other.currency}`),
            );
            return false;
        }
        return true;
    }

    add(other: MonetaryValue): MonetaryValueHelper {
        if (!this.assertCurrency(other, 'add')) {
            return MonetaryValueHelper.zero(this.mv.currency);
        }
        const result = this.safeParseAmount() + MonetaryValueHelper.from(other).safeParseAmount();
        return new MonetaryValueHelper({ amount: String(result), currency: this.mv.currency });
    }

    subtract(other: MonetaryValue): MonetaryValueHelper {
        if (!this.assertCurrency(other, 'subtract')) {
            return MonetaryValueHelper.zero(this.mv.currency);
        }
        const result = this.safeParseAmount() - MonetaryValueHelper.from(other).safeParseAmount();
        return new MonetaryValueHelper({ amount: String(result), currency: this.mv.currency });
    }

    /** Alias for `this.subtract(other)`. */
    diff(other: MonetaryValue): MonetaryValueHelper {
        return this.subtract(other);
    }

    scale(factor: number): MonetaryValueHelper {
        const result = this.safeParseAmount() * factor;
        return new MonetaryValueHelper({ amount: String(result), currency: this.mv.currency });
    }

    toNumber(): number {
        return this.safeParseAmount();
    }

    isZero(): boolean {
        return Math.abs(this.safeParseAmount()) < 0.000001;
    }

    isNegative(): boolean {
        return this.safeParseAmount() < -0.000001;
    }

    isPositive(): boolean {
        return this.safeParseAmount() > 0.000001;
    }

    /** `(this - baseline) / baseline * 100`; null for a zero baseline. Another currency throws (null in production). */
    percentChange(baseline: MonetaryValue): number | null {
        if (!this.assertCurrency(baseline, 'percentChange')) {
            return null;
        }
        const baseAmount = MonetaryValueHelper.from(baseline).safeParseAmount();
        if (Math.abs(baseAmount) < 0.000001) return null;
        const thisAmount = this.safeParseAmount();
        return ((thisAmount - baseAmount) / baseAmount) * 100;
    }

    build(): MonetaryValue {
        return { ...this.mv };
    }

    get currency(): Currency {
        return this.mv.currency;
    }

    static from(mv: MonetaryValue): MonetaryValueHelper {
        return new MonetaryValueHelper(mv);
    }

    static zero(currency: Currency): MonetaryValueHelper {
        return new MonetaryValueHelper({ amount: '0', currency });
    }

    static sum(values: MonetaryValue[]): MonetaryValueHelper {
        if (values.length === 0) {
            throwErrorUnlessProduction(new Error('MonetaryValueHelper.sum: cannot sum empty array (no currency)'));
            return new MonetaryValueHelper({ amount: '0', currency: Currency.EUR });
        }
        const currency = values[0].currency;
        let total = 0;
        for (const v of values) {
            if (v.currency !== currency) {
                throwErrorUnlessProduction(
                    new Error(`MonetaryValueHelper.sum: currency mismatch ${currency} vs ${v.currency}`),
                );
                continue;
            }
            total += MonetaryValueHelper.from(v).safeParseAmount();
        }
        return new MonetaryValueHelper({ amount: String(total), currency });
    }
}
